export const HTTP_ERRORS = {
  UNAUTHORIZED: 'Unauthorized',
  FORBIDDEN: 'Forbidden',
  INVALID_APPROVED_SOFTWARE_ID: 'Invalid approved software ID',
  INVALID_CONNECTOR_ID: 'Invalid connector ID',
  INVALID_FINDING_ID: 'Invalid finding ID',
  INVALID_REQUEST_BODY: 'Invalid request body',
  CHAT_MESSAGE_EMPTY: 'Chat message cannot be empty',
  CHAT_MESSAGE_TOO_LONG: 'Chat message exceeds maximum length',
  MONTHLY_LIMIT_REACHED: 'MONTHLY_LIMIT_REACHED',
  CONVERSATION_BUSY: 'CONVERSATION_BUSY',
} as const

// Optional tag on a service error; only the public API reads it, to tell a caller's mistake from our failure.
export const SERVICE_ERROR_KINDS = {
  USER: 'user' as const,
  NOT_FOUND: 'not_found' as const,
} as const

export type IServiceErrorKind = (typeof SERVICE_ERROR_KINDS)[keyof typeof SERVICE_ERROR_KINDS]

// Thrown by React when a streamed page is cut off, e.g. the user navigates away before it finishes loading.
export const RSC_CONNECTION_CLOSED = 'Connection closed.'

export const CHAT_CLIENT_ERRORS = {
  SERVER_UNAVAILABLE: 'The assistant is temporarily unavailable. Please try again in a moment.',
} as const

export const HTTP_CLIENT_ERRORS = {
  NETWORK: 'Network error occurred',
  UNEXPECTED_RESPONSE: 'The server sent an unexpected response. Please reload the page and try again.',
  SESSION_EXPIRED: 'Your session has expired. Please log in again.',
  FORBIDDEN: 'You do not have permission to perform this action.',
  NOT_FOUND: 'The requested resource was not found.',
  SERVICE_UNAVAILABLE: 'The service is temporarily unavailable. Please try again in a moment.',
  REQUEST_FAILED: 'The request could not be completed. Please try again.',
} as const

export const CHAT_AGENT_ERRORS = {
  TOKEN_LIMIT:
    'That question pulled in too much data for me to process at once. Try narrowing it — for example, filter by severity, status, or a specific system.',
  TIMEOUT: 'That request took too long to complete. Try asking something more specific.',
  GENERIC: 'I could not complete that request. Please try rephrasing your question.',
} as const

export const SERVICE_ERRORS = {
  CONNECTORS_FETCH_FAILED: 'Failed to fetch connectors',
  VENDORS_FETCH_FAILED: 'Failed to fetch vendors',
  APPSEC_REPOSITORIES_FETCH_FAILED: 'Failed to fetch repositories',
  APPSEC_REPOSITORY_INVALID: 'Repository name is required',
  APPSEC_REPOSITORY_CLONE_URL_REQUIRED: 'Clone URL is required for this provider',
  APPSEC_REPOSITORY_CLONE_URL_INVALID: 'Invalid clone URL',
  APPSEC_REPOSITORY_CREATE_FAILED: 'Failed to add repository',
  APPSEC_REPOSITORY_ALREADY_EXISTS: 'Repository already exists',
  APPSEC_REPOSITORY_DELETE_FAILED: 'Failed to remove repository',
  TEXT_DOCUMENT_ALREADY_LINKED: 'That framework document is already linked to another text document',
  TEXT_DOCUMENT_MENTION_NOT_ALLOWED: 'That user cannot be mentioned in this document',
  IT_INVENTORY_HARDWARE_INVALID: 'Item, type, serial number and location are required',
  IT_INVENTORY_HARDWARE_CREATE_FAILED: 'Failed to add the hardware item',
  IT_INVENTORY_HARDWARE_UPDATE_FAILED: 'Failed to update the hardware item',
  IT_INVENTORY_HARDWARE_DELETE_FAILED: 'Failed to delete the hardware item',
  IT_INVENTORY_HARDWARE_NOT_FOUND: 'Hardware item not found',
  IT_INVENTORY_CLOUD_RESOURCE_INVALID: 'The user data flag is required',
  IT_INVENTORY_CLOUD_RESOURCE_UPDATE_FAILED: 'Failed to update the cloud resource',
  IT_INVENTORY_CLOUD_RESOURCE_NOT_FOUND: 'Cloud resource not found',
  CSPM_FINDING_NOT_FOUND: 'Finding not found',
  IT_INVENTORY_HARDWARE_IMPORT_FAILED: 'Failed to import the hardware file',
  REVIEW_ENTRY_LIST_FAILED: 'Failed to load the review history',
  REVIEW_ENTRY_INVALID: 'A valid date is required to log the entry',
  REVIEW_ENTRY_CREATE_FAILED: 'Failed to log the entry',
  REVIEW_ENTRY_FILE_NOT_FOUND: 'This entry has no attached file',
  REVIEW_ENTRY_FILE_FAILED: 'Failed to open the attached file',
  RISK_TREATMENT_PLAN_NOT_FOUND: 'Treatment plan not found',
} as const
